import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { fileToDataUrl } from "@/lib/image";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Loader2, Upload, Save } from "lucide-react";

export default function Profile() {
  const { user, updateUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [avatar, setAvatar] = useState(user?.avatar_url || null);
  const [uploading, setUploading] = useState(false);

  const save = useMutation({
    mutationFn: async () => (await api.put("/author/profile", { name, email, avatar_url: avatar })).data,
    onSuccess: (data) => {
      updateUser({ name: data.name, email: data.email, avatar_url: data.avatar_url });
      toast.success("Profile updated");
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      setAvatar(await fileToDataUrl(f, 400));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const initials = (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <p className="text-xs uppercase tracking-[0.1em] text-muted-foreground">My profile</p>
        <p className="text-sm text-muted-foreground mt-1">Update your name, photo and contact email.</p>
      </div>

      <Card className="p-6 lg:p-8 space-y-6">
        <div className="flex items-center gap-5">
          <Avatar className="h-20 w-20">
            {avatar && <AvatarImage src={avatar} alt={name} />}
            <AvatarFallback className="bg-secondary text-secondary-foreground text-xl font-semibold">{initials}</AvatarFallback>
          </Avatar>
          <div>
            <input id="avatar-file" type="file" accept="image/*" className="hidden" data-testid="avatar-upload" onChange={onFile} />
            <Button variant="outline" className="gap-2" onClick={() => document.getElementById("avatar-file").click()} disabled={uploading}>
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Change photo
            </Button>
            <p className="text-xs text-muted-foreground mt-2">JPG or PNG, resized automatically.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>User ID</Label>
            <Input value={user?.user_id || ""} disabled className="opacity-70" data-testid="profile-userid" />
          </div>
          <div className="space-y-2">
            <Label>Full name</Label>
            <Input data-testid="profile-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label>Contact email</Label>
            <Input data-testid="profile-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
        </div>

        <Button data-testid="save-profile" onClick={() => save.mutate()} disabled={!name.trim() || !email.trim() || save.isPending} className="gap-2">
          {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes
        </Button>
      </Card>
    </div>
  );
}
